import { useState } from 'react';
import type { AccountData } from '@shared/types';

interface Props {
  state: AccountData;
  onChange: (data: AccountData) => void;
  onNext: () => void;
  isLoading: boolean;
}

export default function StapAccount({ state, onChange, onNext, isLoading }: Props) {
  const [errors, setErrors] = useState<Partial<Record<keyof AccountData, string>>>({});

  function validate(): boolean {
    const e: Partial<Record<keyof AccountData, string>> = {};
    if (!state.voornaam.trim() || state.voornaam.trim().length < 2)
      e.voornaam = 'Vul je voornaam in (min. 2 tekens).';
    if (!state.email.trim() || !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(state.email))
      e.email = 'Vul een geldig e-mailadres in.';
    if (!state.akkoordAV)
      e.akkoordAV = 'Je moet akkoord gaan met de algemene voorwaarden.';
    setErrors(e);
    return Object.keys(e).length === 0;
  }

  function handleSubmit(ev: React.FormEvent) {
    ev.preventDefault();
    if (validate()) onNext();
  }

  function field(key: keyof AccountData, value: string) {
    onChange({ ...state, [key]: value });
  }

  return (
    <form className="lc-stap lc-stap--account" onSubmit={handleSubmit} noValidate>
      <h2 className="lc-stap__titel">Jouw gegevens</h2>
      <p className="lc-stap__beschrijving">
        We bewaren je assessment zodat je er altijd bij kunt. Vul je e-mailadres in om te starten.
      </p>

      <div className="lc-veld">
        <label className="lc-veld__label" htmlFor="lc-voornaam">
          Voornaam <span aria-hidden="true">*</span>
        </label>
        <input
          id="lc-voornaam"
          className={`lc-veld__input${errors.voornaam ? ' lc-veld__input--fout' : ''}`}
          type="text"
          autoComplete="given-name"
          value={state.voornaam}
          onChange={(e) => field('voornaam', e.target.value)}
          required
        />
        {errors.voornaam && <p className="lc-veld__fout" role="alert">{errors.voornaam}</p>}
      </div>

      <div className="lc-veld">
        <label className="lc-veld__label" htmlFor="lc-achternaam">
          Achternaam
        </label>
        <input
          id="lc-achternaam"
          className="lc-veld__input"
          type="text"
          autoComplete="family-name"
          value={state.achternaam}
          onChange={(e) => field('achternaam', e.target.value)}
        />
      </div>

      <div className="lc-veld">
        <label className="lc-veld__label" htmlFor="lc-email">
          E-mailadres <span aria-hidden="true">*</span>
        </label>
        <input
          id="lc-email"
          className={`lc-veld__input${errors.email ? ' lc-veld__input--fout' : ''}`}
          type="email"
          autoComplete="email"
          value={state.email}
          onChange={(e) => field('email', e.target.value)}
          required
        />
        {errors.email && <p className="lc-veld__fout" role="alert">{errors.email}</p>}
      </div>

      <div className="lc-veld lc-veld--checkbox">
        <label className="lc-veld__checkbox-label">
          <input
            type="checkbox"
            checked={state.akkoordAV}
            onChange={(e) => onChange({ ...state, akkoordAV: e.target.checked })}
          />
          <span>
            Ik ga akkoord met de{' '}
            <a href="/algemene-voorwaarden/" target="_blank" rel="noopener noreferrer">
              algemene voorwaarden
            </a>{' '}
            en het{' '}
            <a href="/privacybeleid/" target="_blank" rel="noopener noreferrer">
              privacybeleid
            </a>
            .
          </span>
        </label>
        {errors.akkoordAV && (
          <p className="lc-veld__fout" role="alert">{errors.akkoordAV}</p>
        )}
      </div>

      <div className="lc-stap__acties">
        <button
          type="submit"
          className="lc-knop lc-knop--primair"
          disabled={isLoading}
        >
          {isLoading ? 'Even wachten…' : 'Volgende →'}
        </button>
      </div>
    </form>
  );
}
